import { useTranslation } from 'react-i18next'
import { useDraggable } from '@dnd-kit/core'
import { useImageSlots } from '@/hooks/useImageSlots'
import {
  collectRemakeImageGroups,
  type RemakeSlotImage,
} from './remakeImages'

interface RemakeImagesPanelProps {
  remakeId: string | null
}

export function RemakeImagesPanel({ remakeId }: RemakeImagesPanelProps) {
  const { t } = useTranslation()
  const { data: slots, isLoading } = useImageSlots(remakeId ?? undefined)
  const groups = collectRemakeImageGroups(slots)

  if (!remakeId || (!isLoading && groups.length === 0)) {
    return (
      <div className="px-4 py-6 text-xs text-neutral-500 italic">
        {t('remakeApply.images.noImages')}
      </div>
    )
  }

  const total = groups.reduce((n, g) => n + g.images.length, 0)

  return (
    <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-5">
      <div className="text-xs text-neutral-500">
        {isLoading ? '…' : t('remakeApply.images.count', { count: total })}
      </div>
      {groups.map((group) => (
        <section key={group.slotNumber} className="space-y-2">
          <h4 className="text-xs font-medium text-neutral-600 dark:text-neutral-300 capitalize">
            {t('remakeApply.images.slotGroupLabel', {
              n: group.slotNumber,
              purpose: group.slotPurpose.replace(/_/g, ' '),
            })}
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {group.images.map((img) => (
              <DraggableImageCard
                key={img.generationId}
                image={img}
                slotNumber={group.slotNumber}
              />
            ))}
          </div>
        </section>
      ))}
      <div className="text-xs text-neutral-500 text-center mt-1">
        {t('remakeApply.sidebar.dragHint')}
      </div>
    </div>
  )
}

interface DraggableImageCardProps {
  image: RemakeSlotImage
  slotNumber: number
}

function DraggableImageCard({ image, slotNumber }: DraggableImageCardProps) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `remake-img-${image.generationId}`,
    data: {
      generationId: image.generationId,
      assetUrl: image.assetUrl,
      slotNumber,
    },
  })
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`relative aspect-square rounded-lg border overflow-hidden cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-40' : ''
      } ${
        image.isSelected
          ? 'border-orange-500 ring-2 ring-orange-500/30'
          : 'border-neutral-200 dark:border-neutral-700'
      }`}
    >
      <img
        src={image.assetUrl}
        alt={`Slot ${slotNumber}`}
        loading="lazy"
        className="w-full h-full object-cover"
      />
      {image.isSelected && (
        <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
          {t('remakeApply.images.selectedBadge')}
        </span>
      )}
    </div>
  )
}
